import React, { useMemo } from "react";

import { MenuItem, SelectChangeEvent } from "@mui/material";
import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { Input, Select } from "../../../../../../../../../components/Form";
import { ReferenceInputWrapper, ReferenceLoading, Text } from "./styles";
import { colors } from "@zapalacorp/tokens";
import useYupValidation from "../../../../../../../../../hooks/useYupValidation";
import {
  ShipmentLoadType,
  ShipmentStatus,
  ShipmentType,
} from "../../../../../../../../../util/enums";
import { useShipmentContext } from "../../../../hooks/useContext";
import { ViewField } from "../../ViewField";
import { SectionTitle } from "./section-title";
import { LoadingSpinner } from "Components/LoadingModal/styles";

interface GeneralSectionProps {
  isOnView?: boolean;
  errors?: yup.ValidationError[];
  isFetchingReference?: boolean;
}
export function GeneralSection({
  isOnView = false,
  errors = [],
  isFetchingReference,
}: GeneralSectionProps) {
  const { t: translate } = useTranslation();

  const { shipment, setShipment } = useShipmentContext();
  const isEditMode = useMemo(() => !!shipment?.id, [shipment]);
  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  const loadTypeOptions = useMemo(() => {
    switch (shipment?.shipmentType) {
      case ShipmentType.SEA:
        return [
          { label: ShipmentLoadType.FCL, value: ShipmentLoadType.FCL },
          { label: ShipmentLoadType.LCL, value: ShipmentLoadType.LCL },
          { label: ShipmentLoadType.GROUPAGE, value: ShipmentLoadType.GROUPAGE },
          { label: ShipmentLoadType.RORO, value: ShipmentLoadType.RORO },
          { label: ShipmentLoadType.BULK, value: ShipmentLoadType.BULK },
        ];
      case ShipmentType.ROAD:
        return [
          { label: ShipmentLoadType.FTL, value: ShipmentLoadType.FTL },
          { label: ShipmentLoadType.LTL, value: ShipmentLoadType.LTL },
          { label: ShipmentLoadType.GROUPAGE_VANLINE, value: ShipmentLoadType.GROUPAGE_VANLINE },
          { label: ShipmentLoadType.OTHER, value: ShipmentLoadType.OTHER },
        ];
      case ShipmentType.AIR:
        return [
          { label: ShipmentLoadType.CASE, value: ShipmentLoadType.CASE },
          { label: ShipmentLoadType.PALLET, value: ShipmentLoadType.PALLET },
          { label: ShipmentLoadType.BOX, value: ShipmentLoadType.BOX },
          { label: ShipmentLoadType.OTHER, value: ShipmentLoadType.OTHER },
        ];
      default:
        return [{ label: ShipmentLoadType.OTHER, value: ShipmentLoadType.OTHER }];
    }
  }, [shipment?.shipmentType]);

  return (
    <Grid container item xs={12} direction="row" spacing={3}>
      <Grid item xs={12}>
        <SectionTitle title={translate("general")} />
      </Grid>
      <Grid item xs={12} sm={3} lg={3}>
        {isOnView ? (
          <ViewField label={translate("mode")} value={shipment?.shipmentType ? translate(shipment.shipmentType.toLowerCase()) : ""} />
        ) : (
          <Select
            disabled={
              (shipment?.statusShipment !== undefined &&
                shipment.statusShipment !== ShipmentStatus.DRAFT) ||
              isEditMode
            }
            name="type"
            label={translate("mode")}
            value={shipment?.shipmentType}
            placeholder={translate("select")}
            onChange={(e: SelectChangeEvent<any>) =>
              setShipment({
                ...shipment,
                commodity: "",
                voyageNumber: "",
                vesselName: "",
                billOfLanding: "",
                shipmentType: e.target.value,
                fetchedData: false,
                shipmentTracking: {
                  ...shipment?.shipmentTracking,
                  originPort: "",
                  finalPort: "",
                },
              })
            }
            required
            error={fieldHasError("shipmentType")}
            errorMessage={getErrorByField("shipmentType")}
          >
            <MenuItem value={ShipmentType.SEA}>{translate("sea")}</MenuItem>
            <MenuItem value={ShipmentType.AIR}>{translate("air")}</MenuItem>
          </Select>
        )}
      </Grid>
      <Grid item xs={12} sm={3} lg={3}>
        {isOnView ? (
          <ViewField label={translate("reference")} value={shipment?.containerId} />
        ) : (
          <ReferenceInputWrapper style={{ position: "relative" }}>
            <Input
              data-cy="container-id"
              label={`${translate("reference")}`}
              required
              name="containerId"
              disabled={isFetchingReference || (!!shipment?.id && !!shipment?.containerId)}
              value={shipment?.containerId ?? ""}
              onChange={(event) => {
                setShipment({ ...shipment, containerId: event.target.value });
              }}
              error={fieldHasError("containerId")}
              errorMessage={getErrorByField("containerId")}
            />
            {isFetchingReference && (
              <ReferenceLoading>
                <LoadingSpinner size={16} />
              </ReferenceLoading>
            )}
          </ReferenceInputWrapper>
        )}
      </Grid>
      <Grid item xs={12} sm={3} lg={3}>
        {isOnView ? (
          <ViewField label={translate("loadType")} value={shipment?.loadType} />
        ) : (
          <Select
            required
            name="loadType"
            disabled={isEditMode}
            label={translate("loadType")}
            placeholder={translate("selectALoadType")}
            value={shipment?.loadType}
            onChange={(event: SelectChangeEvent<any>) =>
              setShipment({ ...shipment, loadType: event.target.value })
            }
            error={fieldHasError("loadType")}
            errorMessage={getErrorByField("loadType")}
          >
            {loadTypeOptions?.map((option) => (
              <MenuItem key={option.label} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        )}
      </Grid>
      <Grid item xs={12} sm={3} lg={3}>
        {isOnView ? (
          <ViewField label={translate("nickname")} value={shipment?.nickName} />
        ) : (
          <Input
            data-cy="nickname"
            label={translate("nickname")}
            name="nickname"
            placeholder={translate("enterNickname")}
            value={shipment?.nickName ?? ""}
            onChange={(event) => {
              setShipment({ ...shipment, nickName: event.target.value });
            }}
          />
        )}
        <Grid item xs={12} style={{ paddingTop: 4 }}>
          <Text style={{ color: colors.gray500 }}>
            {translate("nicknameIsForYourOwnReference")}
          </Text>
        </Grid>
      </Grid>
    </Grid>
  );
}
